import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import { GreenroomState as GS } from '../../stores/types/types';
import { ContentServiceJobStatus as JobStatus, FkTable } from '../../stores/reducers/generate-content';

const StyledDiv = styled.div`
	@keyframes splFadeIn {
		0% {
			opacity: 0;
		}
		100% {
			opacity: 1;
		}
	}

	@keyframes splFadeOut {
		0% {
			opacity: 1;
		}
		100% {
			opacity: 0;
		}
	}

	@keyframes splSpinner {
 		to {
			-webkit-transform: rotate(360deg);
			transform: rotate(360deg);
		}
	}

	position: absolute;
	top: -16px;
	left: 9px;
	display: flex;
	width: 32px;
	height: 32px;
	padding: 10px;
	justify-content: center;
	align-items: center;
	gap: 6px;
	border-radius: 100px;
	background-color: #4F4C57;
	z-index: 200000;
	animation: splFadeIn 1s ease-in-out forwards;

	&.fade-out {
		animation: splFadeOut 1s ease-in-out forwards;
	}

	:after {
		content: '';
		position: absolute;
		width: 100%;
		height: 100%;
		border-radius: 100px;
		border: 2px solid transparent;
 		border-right-color: #A6FF79;
		animation: splSpinner 1s linear infinite;
	}
`;

interface StylePackLoadingSpinnerProps {
	type: FkTable;
	id: number;
	isForShotEditorModal: boolean;
	previewImage?: string;
	targetElement?: HTMLDivElement | null;
	wrapperStyle?: React.CSSProperties;
}

export default function StylePackLoadingSpinner(props: StylePackLoadingSpinnerProps): JSX.Element {
	const {
		type,
		id,
		isForShotEditorModal,
		previewImage,
		targetElement = null,
		wrapperStyle = {},
	} = props;

	const generatedCon = useSelector((s: GS) => s.GeneratedContentReducer.generatedContent);
	const generatedConEdits = useSelector((s: GS) => s.GeneratedContentReducer.generatedContentEdits);
	const initializingJobs = useSelector((s: GS) => s.GeneratedContentReducer.initializingJobs);

	const [shouldRender, setShouldRender] = useState<boolean>(false);
	const [targetPosition, setTargetPosition] = useState<React.CSSProperties>({});

	const initializing = useMemo(
		() => initializingJobs?.get(`${type}-${id}${isForShotEditorModal ? '-draft' : ''}`),
		[initializingJobs],
	);

	const processing = useMemo(() => {
		if (isForShotEditorModal) {
			return generatedConEdits?.find(
				(j) => j.fkTable === type && j.fkId === id && j.jobStatus === JobStatus.processing,
			);
		}
		return generatedCon?.find(
			(j) => j.fkTable === type && j.fkId === id && j.jobStatus === JobStatus.processing,
		);
	}, [generatedCon, generatedConEdits]);

	const initOrProcessing = useMemo(() => (initializing || processing), [initializing, processing]);

	useEffect(() => {
		if (!shouldRender && initOrProcessing) {
			setShouldRender(true);
		}
	}, [initOrProcessing]);

	useEffect(() => {
		let observer: MutationObserver;

		// if a target element is provided,
		// use it for the spinner's position, size and rotation and update if it changes
		if (targetElement && shouldRender) {
			observer = new MutationObserver((records) => {
				records.forEach((record) => {
					if (record.target instanceof HTMLDivElement) {
						const {
							left,
							top,
							width,
							height,
							transform,
						} = record.target.style;

						setTargetPosition({
							left,
							top,
							width,
							height,
							transform,
						});
					}
				});
			});

			observer.observe(targetElement, {
				attributes: true,
				attributeFilter: ['style'],
			});
		}

		return () => {
			observer?.disconnect();
		};
	}, [targetElement, shouldRender]);

	if (!shouldRender) {
		return <></>;
	}

	return (
		<div style={{ ...wrapperStyle, ...targetPosition }}>
			<StyledDiv
				className={classNames(shouldRender && !initOrProcessing && 'fade-out')}
				onAnimationEnd={(e) => {
					if (e.animationName === 'splFadeOut') {
						setShouldRender(false);
						setTargetPosition({});
					}
				}}
			>
				{
					type === FkTable.graphics && initOrProcessing && !previewImage
						? <Icon name={ICONS.IMAGE} color={COLORS.TEXT_GRAY_400} size={12} />
						: <Icon name={ICONS.PLAY_PADDING} color={COLORS.TEXT_GRAY_400} size={10} />
				}
			</StyledDiv>
		</div>
	);
}
